import type {
  ServiceArgs,
  ServiceInstance,
  ServiceInstanceName,
  ServicesConfig,
  ServicesInstancesNames,
  ServicesNames,
} from './types.js';

export enum ServiceLocatorInstanceHolderKind {
  Instance = 'instance',
  Factory = 'factory',
  AbstractFactory = 'abstractFactory',
}

export enum ServiceLocatorInstanceHolderStatus {
  Created = 'created',
  Creating = 'creating',
  Destroying = 'destroying',
}

export type ServiceLocatorInstanceEffect = () => void;
export type ServiceLocatorInstanceDestroyListener = () => void | Promise<void>;

export interface ServiceLocatorInstanceHolderCreating<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  Services extends ServicesConfig = {},
  Key extends ServicesNames<Services> = ServicesNames<Services>,
  Args extends ServiceArgs<Services, Key> = ServiceArgs<Services, Key>,
> {
  status: ServiceLocatorInstanceHolderStatus.Creating;
  name: ServiceInstanceName<Services, Key>;
  instance: null;
  creationPromise: Promise<[undefined, ServiceInstance<Services, Key, Args>]>;
  destroyPromise: null;
  kind: ServiceLocatorInstanceHolderKind;
  effects: ServiceLocatorInstanceEffect[];
  deps: ServicesInstancesNames<Services>[];
  destroyListeners: ServiceLocatorInstanceDestroyListener[];
  createdAt: number;
  ttl: number;
}

export interface ServiceLocatorInstanceHolderCreated<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  Services extends ServicesConfig = {},
  Key extends ServicesNames<Services> = ServicesNames<Services>,
  Args extends ServiceArgs<Services, Key> = ServiceArgs<Services, Key>,
> {
  status: ServiceLocatorInstanceHolderStatus.Created;
  name: ServiceInstanceName<Services, Key>;
  instance: ServiceInstance<Services, Key, Args>;
  creationPromise: null;
  destroyPromise: null;
  kind: ServiceLocatorInstanceHolderKind;
  effects: ServiceLocatorInstanceEffect[];
  deps: ServicesInstancesNames<Services>[];
  destroyListeners: ServiceLocatorInstanceDestroyListener[];
  createdAt: number;
  ttl: number;
}

export interface ServiceLocatorInstanceHolderDestroying<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  Services extends ServicesConfig = {},
  Key extends ServicesNames<Services> = ServicesNames<Services>,
  Args extends ServiceArgs<Services, Key> = ServiceArgs<Services, Key>,
> {
  status: ServiceLocatorInstanceHolderStatus.Destroying;
  name: ServiceInstanceName<Services, Key>;
  instance: ServiceInstance<Services, Key, Args> | null;
  creationPromise: null;
  destroyPromise: Promise<void>;
  kind: ServiceLocatorInstanceHolderKind;
  effects: ServiceLocatorInstanceEffect[];
  deps: ServicesInstancesNames<Services>[];
  destroyListeners: ServiceLocatorInstanceDestroyListener[];
  createdAt: number;
  ttl: number;
}

export type ServiceLocatorInstanceHolder<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  Services extends ServicesConfig = {},
  Key extends ServicesNames<Services> = ServicesNames<Services>,
  Args extends ServiceArgs<Services, Key> = ServiceArgs<Services, Key>,
> =
  | ServiceLocatorInstanceHolderCreating<Services, Key, Args>
  | ServiceLocatorInstanceHolderCreated<Services, Key, Args>
  | ServiceLocatorInstanceHolderDestroying<Services, Key, Args>;
